/**
 * Shared helpers for the cached `/fieldvalues` payload.
 *
 * The cache keys are versioned: bump the suffix whenever the API's field-value
 * lists change in a way the UI depends on, so returning users pick up the new
 * lists immediately instead of waiting out the 24 hour cache.
 */
export const FIELD_VALUES_CACHE_KEY = "uwillFieldvalues_v2";
export const FIELD_VALUES_CACHE_TIMESTAMP_KEY =
  "uwillFieldvaluesCacheTimestamp_v2";

const PREFER_NOT_TO_SAY = "prefer not to say";

/**
 * Drops the cached payload so the next fetch is made as the signed-in user.
 * The API filters these lists per organization, so a payload cached before
 * sign-in can be the wrong list for this user.
 */
export function clearFieldValuesCache() {
  localStorage.removeItem(FIELD_VALUES_CACHE_TIMESTAMP_KEY);
  localStorage.removeItem(FIELD_VALUES_CACHE_KEY);
}

/**
 * Sorts ethnicities alphabetically, with "Prefer not to say" pinned to the top.
 * Sorts a copy so the cached payload keeps the order the API sent it in, which
 * is what the submit paths map selections back to.
 */
export function sortEthnicities(ethnicities) {
  return [...(ethnicities || [])].sort((a, b) => {
    if (a.toLowerCase() === PREFER_NOT_TO_SAY) return -1;
    if (b.toLowerCase() === PREFER_NOT_TO_SAY) return 1;
    return a.localeCompare(b);
  });
}

/**
 * Drops "Prefer not to say", which is offered to providers only.
 *
 * Display-only, like sortEthnicities: the submit paths map selections back
 * through the unfiltered cached payload, so the indices the API resolves
 * against are unaffected. Accepts either the raw labels the API sends or the
 * { value, text } options the forms build from them.
 */
export function selectableEthnicities(ethnicities, { provider = false } = {}) {
  if (provider) return [...(ethnicities || [])];

  return (ethnicities || []).filter(ethnicity => {
    const label =
      typeof ethnicity === "object" && ethnicity !== null
        ? ethnicity.value ?? ethnicity.text
        : ethnicity;

    return String(label).toLowerCase() !== PREFER_NOT_TO_SAY;
  });
}
